import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { convertValueNotRounded } from '@utils/helper/convertValue';
import { roundFloatNumber } from '@utils/helper/roundFloatNumber';
import { CurrencyItem } from '@utils/models/Currency';

import { crmModels } from 'modules/crm';

import { helper } from '../../utils/helper/helper';
import { ContactCrm, FieldValue, OneLid } from '../../utils/models/OneLid';
import { PipeStatuses } from '../../utils/models/PipeAll';

interface CrmOneLidState {
  tags: crmModels.ITags['tagList'];
  contacts: ContactCrm[];
  balance: number;
  balanceString: string;
  pipeStatusId: number;
  oldPipeStatusId: string;
  pipeId: number;
  responsibleById: number | undefined;
  leadFieldValue: FieldValue[];
  pipeName: string;
  oldPipeName: string;
  pipeStatusName: string;
  oldPipeStatusName: string;
  isChange: boolean;
  colorStatus: string;
  isAddContact: boolean;
  id: number | undefined;
  name: string;
  isMoiZvonki: boolean;
  showReasonRefusal: boolean;
  showComplete: boolean;
  clientId: number;
  clientPhone: string;
  clientName: string;
  clientIsVisible: boolean;
  openDublicateModal: boolean;
  openConfirmModal: boolean;
  dublicateClientId: number | undefined;
  isResponsible: boolean;
  leadFieldValueAi: string[];
  loadingNoteIds: number[];
  currencyDto?: CurrencyItem;
  isHasTask: boolean;
}

const initialState: CrmOneLidState = {
  balanceString: '0',
  tags: [],
  contacts: [],
  balance: 0,
  pipeStatusId: 0,
  oldPipeStatusId: '0',
  leadFieldValue: [],
  pipeName: '',
  responsibleById: undefined,
  isChange: false,
  pipeId: 0,
  pipeStatusName: '',
  oldPipeStatusName: '',
  colorStatus: '',
  isAddContact: false,
  id: undefined,
  name: '',
  isMoiZvonki: false,
  showReasonRefusal: false,
  showComplete: false,
  oldPipeName: '',
  clientId: -1,
  clientName: '',
  clientPhone: '',
  clientIsVisible: false,
  openDublicateModal: false,
  openConfirmModal: false,
  dublicateClientId: undefined,
  isResponsible: false,
  leadFieldValueAi: [],
  loadingNoteIds: [],
  isHasTask: false
};

export const CrmOneLidSlice = createSlice({
  name: 'CrmOneLidSlice',
  initialState,
  reducers: {
    setCrmOneLid: (
      state,
      action: {
        payload: OneLid;
      }
    ) => {
      const contacts = [...action.payload.contacts];

      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].phones.length === 0) {
          contacts[i].phones.push('');
        }
      }

      state.balance = action.payload.balance;
      state.balanceString = action.payload.balance.toLocaleString('ru');
      state.contacts = contacts;
      state.tags = action.payload.tags;
      state.pipeName = action.payload.pipeStatusResponse.pipeName || '';
      state.leadFieldValue = action.payload.fields;
      state.pipeStatusId = action.payload.pipeStatusResponse.id || 0;
      state.responsibleById = action.payload.responsibleById;
      state.isChange = false;
      state.pipeId = action.payload.pipeStatusResponse.pipeId;
      state.pipeStatusName = action.payload.pipeStatusResponse.name;
      state.colorStatus = action.payload.pipeStatusResponse.color;
      state.isAddContact = false;
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.isMoiZvonki = action.payload.isMoiZvonki;
      state.currencyDto = action.payload?.currencyDto;
    },
    setNewLid: state => {
      state.balance = 0;
      state.balanceString = '0';
      state.contacts = [
        {
          name: '',
          fields: [],
          id: null,
          phones: ['']
        }
      ];
      state.tags = [];
      state.pipeName = '';
      state.leadFieldValue = [];
      state.pipeStatusId = 0;
      state.responsibleById = undefined;
      state.isChange = true;
      state.isAddContact = true;
      state.id = undefined;
      state.name = '';
      state.showReasonRefusal = false;
      state.showComplete = false;
    },
    setChangeLidName: (state, action: { payload: string }) => {
      state.name = action.payload;
      state.isChange = true;
    },
    setChangePipeStatus: (state, action: { payload: PipeStatuses }) => {
      state.pipeStatusId = action.payload.id || 0;
      state.pipeStatusName = action.payload.name;
      state.colorStatus = action.payload.color;
      state.pipeId = action.payload.pipeId;
      state.pipeName = action.payload.pipeName || '';
      state.showReasonRefusal = action.payload.type === 'FAILED';
      state.showComplete = action.payload.type === 'SUCCESS';
      state.isChange = true;
    },
    setChangePipeStatusClear: state => {
      state.pipeStatusId = 0;
      state.pipeStatusName = '';
      state.colorStatus = '';
      state.pipeId = 0;
      state.pipeName = '';
      state.showReasonRefusal = false;
      state.showComplete = false;
      state.isChange = false;
    },
    setchangeOldPipeStatus: (state, action: { payload: PipeStatuses }) => {
      state.oldPipeStatusId = String(action.payload.id);
      state.oldPipeStatusName = action.payload.name;
      state.oldPipeName = action.payload.pipeName || '';
    },
    setTags: (state, action: { payload: crmModels.ITags['tagList'] }) => {
      state.tags = action.payload;
      state.isChange = true;
    },
    setTagsNotChange: (state, action: { payload: crmModels.ITags['tagList'] }) => {
      state.tags = action.payload;
    },
    setEmployeeId: (state, action: { payload: number }) => {
      state.responsibleById = action.payload;
      state.isChange = true;
      if (action.payload) {
        state.isResponsible = true;
      } else {
        state.isResponsible = false;
      }
    },
    setBalance: (state, action: { payload: string }) => {
      state.balance = helper.parseFloatStringToNumber(action.payload);
      state.balanceString = action.payload;
      state.isChange = true;
    },
    setCurrecyDto: (state, action: { payload?: CurrencyItem }) => {
      const { currencyDto, balance } = current(state);

      state.currencyDto = action.payload;
      state.balance = convertValueNotRounded(currencyDto, action.payload, balance);
      state.balanceString = roundFloatNumber(
        convertValueNotRounded(currencyDto, action.payload, balance)
      ).toLocaleString('ru');
      state.isChange = true;
    },
    changeLeadsFiledValue: (
      state,
      action: {
        payload: {
          id: number;
          value: string;
          valueType?: string;
          ai?: boolean;
          key?: string;
        };
      }
    ) => {
      const fieldsAi = [...state.leadFieldValueAi];
      const fields = [...state.leadFieldValue];
      let isFind = false;

      for (let i = 0; i < fields.length; i++) {
        if (fields[i].id === action.payload.id) {
          fields[i].value = action.payload.value;
          fields[i].valueType = action.payload.valueType;
          isFind = true;
        }
      }
      if (!isFind) {
        fields.push({
          value: action.payload.value,
          id: action.payload.id,
          valueType: action.payload.valueType
        });
      }
      if (action?.payload?.ai) {
        fieldsAi.push(`${action?.payload?.id}:${action?.payload?.key}`);
        state.leadFieldValueAi = fieldsAi;
      }
      state.leadFieldValue = fields;
      state.isChange = true;
    },
    leadFieldFilterAi: (
      state,
      action: {
        payload: string;
      }
    ) => {
      const { leadFieldValueAi } = current(state);

      state.leadFieldValueAi = leadFieldValueAi?.filter(item => item !== action?.payload);
    },
    addLeadFieldAi: (
      state,
      action: {
        payload: string;
      }
    ) => {
      const { leadFieldValueAi } = current(state);
      const arr = [...leadFieldValueAi, action.payload];

      state.leadFieldValueAi = arr;
    },
    changeLeadFiledVal: (
      state,
      action: {
        payload: {
          id: number;
          value: string;
          valueType?: string;
        };
      }
    ) => {
      const fields = [...state.leadFieldValue];
      let isFind = false;

      for (let i = 0; i < fields.length; i++) {
        if (fields[i].id === action.payload.id && fields[i].valueType === action.payload.valueType) {
          fields[i].value = action.payload.value;
          fields[i].valueType = action.payload.valueType;
          isFind = true;
        }
      }
      if (!isFind) {
        fields.push({
          value: action.payload.value,
          id: action.payload.id,
          valueType: action.payload.valueType
        });
      }
      state.leadFieldValue = fields;
      state.isChange = true;
    },
    changeLeadsFiledValueList: (
      state,
      action: {
        payload: {
          id: number;
          valueList: string[];
          ai?: boolean;
          key?: string;
        };
      }
    ) => {
      const fields = [...state.leadFieldValue];
      const fieldsAi = [...state.leadFieldValueAi];

      let isFind = false;

      for (let i = 0; i < fields.length; i++) {
        if (fields[i].id === action.payload.id) {
          fields[i].valueList = action.payload.valueList;
          isFind = true;
        }
      }
      if (!isFind) {
        fields.push({
          valueList: action.payload.valueList,
          id: action.payload.id
        });
      }

      if (action?.payload?.ai) {
        fieldsAi.push(`${action?.payload?.id}:${action?.payload?.valueList[0]}`);
        state.leadFieldValueAi = fieldsAi;
      }

      state.leadFieldValue = fields;
      state.isChange = true;
    },
    changeContactsFiledValue: (
      state,
      action: {
        payload: {
          contactId: number | null;
          id: number;
          value: string;
          ai?: boolean;
          key?: string;
        };
      }
    ) => {
      const contacts = [...state.contacts];
      const fieldsAi = [...state.leadFieldValueAi];

      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].id === action.payload.contactId) {
          let isFind = false;
          const values = contacts[i].fields ? [...contacts[i].fields] : [];

          for (let j = 0; j < values.length || 0; j++) {
            if (values[j].id === action.payload.id) {
              values[j].value = action.payload.value;
              isFind = true;
            }
          }
          if (!isFind) {
            values.push({
              value: action.payload.value,
              id: action.payload.id
            });
          }

          contacts[i].fields = values;
        }
      }

      if (action?.payload?.ai) {
        fieldsAi.push(`${action?.payload?.id}:${action?.payload?.key}:${action?.payload?.contactId}`);
        state.leadFieldValueAi = fieldsAi;
      }
      state.contacts = contacts;
      state.isChange = true;
    },
    changeContactsFiledValueList: (
      state,
      action: {
        payload: {
          contactId: number | null;
          id: number;
          valueList: string[];
          ai?: boolean;
          key?: string;
        };
      }
    ) => {
      const contacts = [...state.contacts];
      const fieldsAi = [...state.leadFieldValueAi];

      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].id === action.payload.contactId) {
          let isFind = false;
          const values = contacts[i].fields ? [...contacts[i].fields] : [];

          for (let j = 0; j < values.length || 0; j++) {
            if (values[j].id === action.payload.id) {
              values[j].valueList = action.payload.valueList;
              isFind = true;
            }
          }
          if (!isFind) {
            values.push({
              valueList: action.payload.valueList,
              id: action.payload.id
            });
          }

          contacts[i].fields = values;
        }
      }

      if (action?.payload?.ai) {
        fieldsAi.push(`${action?.payload?.id}:${action?.payload?.key}:${action?.payload?.contactId}`);
        state.leadFieldValueAi = fieldsAi;
      }
      state.contacts = contacts;
      state.isChange = true;
    },
    changeContactName: (
      state,
      action: {
        payload: {
          contactId: number | null;
          value: string;
          ai?: boolean;
          key?: string;
        };
      }
    ) => {
      const contacts = [...state.contacts];
      const fieldsAi = [...state.leadFieldValueAi];

      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].id === action.payload.contactId) {
          contacts[i].name = action.payload.value;
        }
      }

      if (action?.payload?.ai) {
        fieldsAi.push(`${action?.payload?.contactId}:${action?.payload?.key}`);
        state.leadFieldValueAi = fieldsAi;
      }
      state.contacts = contacts;
      state.isChange = true;
    },
    addContact: state => {
      const contacts = [...state.contacts];

      contacts.push({
        name: '',
        fields: [],
        id: null,
        phones: ['']
      });
      state.isAddContact = true;
      state.contacts = contacts;
      state.isChange = true;
    },
    addPhoneInsideContact: (
      state,
      action: {
        payload: {
          indexContact: number;
        };
      }
    ) => {
      let { isChange } = state;

      const newContacts: ContactCrm[] = [];
      const oldContacts = current(state).contacts;

      for (let i = 0; i < oldContacts.length; i++) {
        if (i !== action.payload.indexContact) {
          newContacts.push(oldContacts[i]);
        } else {
          const phones: string[] = [];

          for (let j = 0; j < oldContacts[i].phones.length; j++) {
            phones.push(oldContacts[i].phones[j]);
          }

          if (phones[phones.length - 1]) {
            phones.push('');
            isChange = true;
          }

          newContacts.push({
            ...oldContacts[i],
            phones
          });
        }
      }

      state.contacts = newContacts;
      state.isChange = isChange;
    },
    removeContactPhone: (state, action: PayloadAction<{ indexContact: number; deleteIndex: number }>) => {
      state.contacts[action.payload.indexContact].phones = state.contacts[action.payload.indexContact].phones?.filter(
        (_, i) => i !== action.payload.deleteIndex
      );
      state.isChange = true;
    },
    changePhoneInsideContact: (
      state,
      action: {
        payload: {
          indexContact: number;
          indexPhone: number;
          value: string;
        };
      }
    ) => {
      const newContacts: ContactCrm[] = [];
      const oldContacts = current(state).contacts;

      for (let i = 0; i < oldContacts.length; i++) {
        if (i !== action.payload.indexContact) {
          newContacts.push(oldContacts[i]);
        } else {
          const phones: string[] = [];

          for (let j = 0; j < oldContacts[i].phones.length; j++) {
            if (j !== action.payload.indexPhone) {
              phones.push(oldContacts[i].phones[j]);
            } else {
              phones.push(action.payload.value);
            }
          }

          newContacts.push({
            ...oldContacts[i],
            phones
          });
        }
      }

      state.contacts = newContacts;
      state.isChange = true;
    },
    removeContact: (state, action: { payload: number | null }) => {
      const contacts = [...state.contacts];

      state.contacts = contacts.filter(item => item.id !== action.payload);
      if (action.payload === null) {
        state.isAddContact = false;
      }
      state.isChange = true;
    },
    setOpenClientMessage: (
      state,
      action: {
        payload: { id: number; name: string; phone: string; isVisible: boolean };
      }
    ) => {
      state.clientIsVisible = action.payload.isVisible;
      state.clientId = action.payload.id;
      state.clientName = action.payload.name;
      state.clientPhone = action.payload.phone;
    },
    setOpenDublicateModal: (state, action: { payload: boolean }) => {
      state.openDublicateModal = action.payload;
    },
    setOpenConfirmModal: (state, action: { payload: boolean }) => {
      state.openConfirmModal = action.payload;
    },
    setChangeDublicateClientId: (state, action: { payload: number | undefined }) => {
      state.dublicateClientId = action.payload;
    },
    setChangeResponsible: state => {
      state.isResponsible = false;
    },
    setLoadingNoteId: (state, action: { payload: number }) => {
      state.loadingNoteIds = [...state.loadingNoteIds, action.payload];
    },
    setLoadingNoteIdFilter: (state, action: { payload: number }) => {
      state.loadingNoteIds = [...state.loadingNoteIds].filter(item => item !== action.payload);
    },
    setCrmChange: (state, action: { payload: boolean }) => {
      state.isChange = action.payload;
    },
    setIsHasTask: (state, action: { payload: boolean }) => {
      state.isHasTask = action.payload;
    }
  }
});

export const {
  setCrmOneLid,
  setChangePipeStatus,
  setTags,
  setEmployeeId,
  setBalance,
  changeLeadsFiledValue,
  changeContactsFiledValue,
  changeContactName,
  addContact,
  setChangeLidName,
  setNewLid,
  addPhoneInsideContact,
  changePhoneInsideContact,
  removeContact,
  setchangeOldPipeStatus,
  changeLeadsFiledValueList,
  changeLeadFiledVal,
  setOpenDublicateModal,
  setChangeDublicateClientId,
  setOpenClientMessage,
  setOpenConfirmModal,
  setChangePipeStatusClear,
  setChangeResponsible,
  leadFieldFilterAi,
  addLeadFieldAi,
  setLoadingNoteId,
  setLoadingNoteIdFilter,
  setTagsNotChange,
  changeContactsFiledValueList,
  setCurrecyDto,
  setCrmChange,
  removeContactPhone,
  setIsHasTask
} = CrmOneLidSlice.actions;
export default CrmOneLidSlice.reducer;
